// OAuthButtons — Google / Microsoft sign-in buttons. Only shown when
// the corresponding VITE_ENABLE_OAUTH_<provider> env-var is "true".
//
// Wires through useAuth().signInWithOAuth which calls
// supabase.auth.signInWithOAuth. The Supabase project still has to
// have the provider configured under Authentication → Providers; this
// component just surfaces the UI.

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { rememberAuthMethod, readLastAuthMethod } from '@/lib/lastAuthMethod';

const GOOGLE_ENABLED = import.meta.env.VITE_ENABLE_OAUTH_GOOGLE === 'true';
const AZURE_ENABLED = import.meta.env.VITE_ENABLE_OAUTH_AZURE === 'true';
const GITHUB_ENABLED = import.meta.env.VITE_ENABLE_OAUTH_GITHUB === 'true';

// #54: detect common OAuth-failure patterns (popup blocked, browser
// extension blocked, network) and surface a clear message instead of
// leaving the user staring at a button that just stopped spinning.
function explainOAuthFailure(err: string, provider: string): string {
  const lower = err.toLowerCase();
  if (lower.includes('popup') || lower.includes('blocked')) {
    return `${provider}-fönstret blockerades av webbläsaren. Tillåt popup-fönster för den här sidan och försök igen.`;
  }
  if (lower.includes('network') || lower.includes('fetch')) {
    return `Nätverksfel vid ${provider}-inloggning. Kontrollera anslutningen och försök igen.`;
  }
  return err;
}

export function OAuthButtons() {
  const { signInWithOAuth } = useAuth();
  const [busy, setBusy] = useState<string | null>(null);
  const lastMethod = readLastAuthMethod();

  const trigger = (p: 'google' | 'azure' | 'github') => async () => {
    setBusy(p);
    // Optimistiskt: kom ihåg innan redirect så hint visas vid retur.
    rememberAuthMethod(p);
    const result = await signInWithOAuth(p);
    if (result.error) {
      setBusy(null);
      const providerLabel = p === 'google' ? 'Google' : p === 'azure' ? 'Microsoft' : 'GitHub';
      toast({
        title: 'Inloggning misslyckades',
        description: explainOAuthFailure(result.error, providerLabel),
        variant: 'destructive',
      });
    }
    // Otherwise the browser redirects to the provider; no cleanup needed.
  };

  const lastHint = (p: 'google' | 'azure' | 'github') =>
    lastMethod === p ? (
      <span className="ml-auto text-[10px] uppercase font-medium text-primary">Senast</span>
    ) : null;

  // When no provider is enabled, render nothing so the divider doesn't
  // dangle below an empty section.
  if (!GOOGLE_ENABLED && !AZURE_ENABLED && !GITHUB_ENABLED) return null;

  return (
    <div className="space-y-2" data-testid="oauth-buttons">
      <div className="relative my-3">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-[10px] uppercase">
          <span className="bg-background px-2 text-muted-foreground">eller</span>
        </div>
      </div>

      {GOOGLE_ENABLED && (
        <Button
          type="button"
          variant="outline"
          className="w-full justify-start gap-2"
          onClick={trigger('google')}
          disabled={busy !== null}
          data-testid="oauth-google"
        >
          {busy === 'google'
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <GoogleIcon className="w-4 h-4" />}
          Logga in med Google
          {lastHint('google')}
        </Button>
      )}
      {AZURE_ENABLED && (
        <Button
          type="button"
          variant="outline"
          className="w-full justify-start gap-2"
          onClick={trigger('azure')}
          disabled={busy !== null}
          data-testid="oauth-azure"
        >
          {busy === 'azure'
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <MicrosoftIcon className="w-4 h-4" />}
          Logga in med Microsoft
          {lastHint('azure')}
        </Button>
      )}
      {GITHUB_ENABLED && (
        <Button
          type="button"
          variant="outline"
          className="w-full justify-start gap-2"
          onClick={trigger('github')}
          disabled={busy !== null}
          data-testid="oauth-github"
        >
          {busy === 'github'
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <GithubIcon className="w-4 h-4" />}
          Logga in med GitHub
          {lastHint('github')}
        </Button>
      )}
    </div>
  );
}

// Provider mini-icons inline so we don't pull in a logo dep.
function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0 0 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18A10.99 10.99 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.83z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z" />
    </svg>
  );
}

function MicrosoftIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path fill="#F25022" d="M2 2h10v10H2z" />
      <path fill="#7FBA00" d="M12 2h10v10H12z" />
      <path fill="#00A4EF" d="M2 12h10v10H2z" />
      <path fill="#FFB900" d="M12 12h10v10H12z" />
    </svg>
  );
}

function GithubIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="currentColor">
      <path d="M12 .3a12 12 0 0 0-3.8 23.4c.6.1.83-.26.83-.59v-2c-3.34.73-4.04-1.6-4.04-1.6-.55-1.39-1.34-1.76-1.34-1.76-1.08-.74.08-.73.08-.73 1.2.09 1.83 1.24 1.83 1.24 1.08 1.84 2.81 1.31 3.5 1 .1-.78.42-1.31.76-1.61-2.66-.3-5.47-1.33-5.47-5.93 0-1.31.47-2.38 1.24-3.22-.13-.3-.54-1.52.12-3.18 0 0 1-.32 3.3 1.23a11.5 11.5 0 0 1 6 0c2.28-1.55 3.29-1.23 3.29-1.23.66 1.66.25 2.88.12 3.18a4.65 4.65 0 0 1 1.24 3.22c0 4.61-2.81 5.62-5.48 5.92.43.37.81 1.1.81 2.22v3.29c0 .32.21.7.84.59A12 12 0 0 0 12 .3" />
    </svg>
  );
}
